interface Tile {
  url: string;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface StoryboardResult {
  url: string;
  width: number;
  height: number;
  tilesInSource: number;
  tilesKept: number;
}

interface StoryboardOptions {
  targetHeight?: number;
  maxWidth?: number;
  maxTiles?: number;
  quality?: number;
}

const DEFAULT_OPTIONS: Required<StoryboardOptions> = {
  targetHeight: 80,
  maxWidth: 600,
  maxTiles: 200,
  quality: 0.9,
};

class ImageCache {
  private cache = new Map<string, Promise<HTMLImageElement>>();

  async load(url: string): Promise<HTMLImageElement> {
    if (!this.cache.has(url)) {
      this.cache.set(url, this.loadImage(url));
    }
    return this.cache.get(url)!;
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }

  clear(): void {
    this.cache.clear();
  }
}

const imageCache = new ImageCache();

async function fetchVTT(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: Failed to fetch ${url}`);
  }
  return response.text();
}

function parseVTT(vtt: string): Tile[] {
  const lines = vtt.split(/\r?\n/);
  const tiles: Tile[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!/-->/.test(line)) continue;

    const nextLine = (lines[i + 1] || '').trim();
    const match = nextLine.match(/^(.+)#xywh=(\d+),(\d+),(\d+),(\d+)$/);

    if (match) {
      tiles.push({
        url: match[1],
        sx: parseInt(match[2], 10),
        sy: parseInt(match[3], 10),
        sw: parseInt(match[4], 10),
        sh: parseInt(match[5], 10),
      });
    }
  }

  return tiles;
}

function calculateDimensions(
  tiles: Tile[],
  options: Required<StoryboardOptions>,
): { outTileW: number; outTileH: number; keepCount: number } {
  const { sw: tileW, sh: tileH } = tiles[0];
  const scale = options.targetHeight / tileH;

  const outTileW = Math.max(1, Math.round(tileW * scale));
  const outTileH = Math.max(1, Math.round(tileH * scale));

  const capacity = Math.max(1, Math.floor(options.maxWidth / outTileW));
  const keepCount = Math.max(
    1,
    Math.min(capacity, options.maxTiles, tiles.length),
  );

  return { outTileW, outTileH, keepCount };
}

function selectTileIndices(totalTiles: number, keepCount: number): number[] {
  if (keepCount >= totalTiles) {
    return Array.from({ length: totalTiles }, (_, i) => i);
  }

  const indices: number[] = [];
  const step = (totalTiles - 1) / (keepCount - 1);

  for (let i = 0; i < keepCount; i++) {
    const idx = Math.round(i * step);
    if (!indices.length || indices[indices.length - 1] !== idx) {
      indices.push(idx);
    }
  }

  // Handle edge case where rounding causes fewer indices than needed
  while (indices.length < keepCount) {
    const lastIdx = indices[indices.length - 1] ?? 0;
    const nextIdx = Math.min(totalTiles - 1, lastIdx + 1);
    if (indices[indices.length - 1] !== nextIdx) {
      indices.push(nextIdx);
    } else {
      break;
    }
  }

  return indices;
}

function createCanvas(
  width: number,
  height: number,
): HTMLCanvasElement | OffscreenCanvas {
  const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';

  if (hasOffscreenCanvas) {
    return new OffscreenCanvas(width, height);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function setupCanvasContext(
  canvas: HTMLCanvasElement | OffscreenCanvas,
): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D rendering context');
  }

  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) {
    ctx.imageSmoothingQuality = 'high';
  }

  return ctx;
}

async function renderTiles(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  tiles: Tile[],
  selectedIndices: number[],
  outTileW: number,
  outTileH: number,
): Promise<void> {
  for (let i = 0; i < selectedIndices.length; i++) {
    const tile = tiles[selectedIndices[i]];
    const img = await imageCache.load(tile.url);
    const dx = i * outTileW;

    ctx.drawImage(
      img,
      tile.sx,
      tile.sy,
      tile.sw,
      tile.sh,
      dx,
      0,
      outTileW,
      outTileH,
    );
  }
}

async function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  quality: number,
): Promise<Blob> {
  const options = { type: 'image/jpeg', quality };

  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob(options);
  }

  return new Promise((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      options.type,
      options.quality,
    );
  });
}

export async function composeMuxStoryboardStrip(
  playbackId: string,
  userOptions: StoryboardOptions = {},
): Promise<StoryboardResult> {
  const options = { ...DEFAULT_OPTIONS, ...userOptions };

  try {
    const vttUrl = `https://image.mux.com/${playbackId}/storyboard.vtt`;
    const vtt = await fetchVTT(vttUrl);
    const tiles = parseVTT(vtt);

    if (!tiles.length) {
      throw new Error('No tiles found in storyboard VTT');
    }

    const { outTileW, outTileH, keepCount } = calculateDimensions(
      tiles,
      options,
    );
    const selectedIndices = selectTileIndices(tiles.length, keepCount);

    const outputWidth = keepCount * outTileW;
    const outputHeight = outTileH;

    const canvas = createCanvas(outputWidth, outputHeight);
    const ctx = setupCanvasContext(canvas);

    await renderTiles(ctx, tiles, selectedIndices, outTileW, outTileH);

    const blob = await canvasToBlob(canvas, options.quality);
    const url = URL.createObjectURL(blob);

    return {
      url,
      width: outputWidth,
      height: outputHeight,
      tilesInSource: tiles.length,
      tilesKept: selectedIndices.length,
    };
  } catch (error) {
    throw new Error(
      `Failed to compose storyboard: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    );
  }
}
